import Delta from './typography/Delta'
import Body from './typography/Body'
import Button from './Button'
import { useRouter } from 'next/router'

import { useSimplified } from '~/context/SimplifiedContext'

export default function WarningBody({
  title,
  type,
  iconColour,
  buttons,
  ...props
}) {
  const router = useRouter()
  // Embedded users can't manage their subscription, so the upgrade button is
  // dropped in the simplified view — the message above it still explains why
  // the quiz is unavailable. Non-upgrade buttons (e.g. /quizzes) are kept.
  const isSimplified = useSimplified()
  const isUpgradeLink =
    (buttons?.primary?.url ?? '').replace(/^\//, '') === 'subscription'
  const showPrimary =
    Boolean(buttons?.primary) && !(isSimplified && isUpgradeLink)
  return (
    <div className="flex flex-col mb-10 md:mb-20 w-full">
      <div className="flex">
        {type && (
          <div
            className="flex-none w-[32px] h-[32px] sr rounded-full flex justify-center items-center mr-10 md:mr-20"
            style={{
              backgroundColor: iconColour
            }}
          >
            {type === 'music' && (
              <svg
                width="15"
                height="20"
                viewBox="0 0 85 85"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M84.6048 16.3341C84.8525 15.5773 84.9261 14.7512 84.7862 13.9114C84.7435 13.6555 84.6826 13.4074 84.6048 13.1679V6.13375C84.6128 5.8139 84.5907 5.48867 84.5362 5.16135C84.5355 5.15711 84.5347 5.15288 84.534 5.14864C84.2846 3.67425 83.4256 2.45463 82.2587 1.70505C81.9242 1.48986 81.563 1.31266 81.1811 1.17956C80.5089 0.944698 79.7793 0.847925 79.0321 0.917702C78.8757 0.931936 78.7213 0.953225 78.5693 0.98125L29.773 9.11397C27.489 9.43997 25.6925 11.2779 25.4296 13.5816C25.4076 13.7734 25.3964 13.9677 25.3965 14.1639V14.1667V30.4939V30.4961V51.4313C23.2173 50.4454 20.7981 49.8965 18.2507 49.8965C8.66621 49.8965 0.896484 57.6662 0.896484 67.2506C0.896484 76.8351 8.66621 84.6048 18.2507 84.6048C27.8351 84.6048 35.6048 76.8351 35.6048 67.2506V67.2503V67.25V34.8251L74.3965 28.3599V43.2643C72.2173 42.2784 69.7981 41.7295 67.2506 41.7295C57.6662 41.7295 49.8965 49.4992 49.8965 59.0837C49.8965 68.6681 57.6662 76.4378 67.2506 76.4378C76.8314 76.4378 84.5988 68.6741 84.6048 59.0948C84.6048 59.0912 84.6048 59.0876 84.6048 59.084L84.6048 59.0837L84.6048 22.4677C84.6069 22.3833 84.6069 22.2985 84.6048 22.2134V16.3341Z"
                  fill="#fff"
                />
              </svg>
            )}
            {type === 'league' && (
              <svg
                width="20"
                height="25"
                viewBox="0 0 50 57"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mx-auto"
              >
                <path
                  d="M28.3057 16.818L16.7826 4.69195C16.5518 4.46127 16.1853 4.45193 15.949 4.6707L3.82297 16.1937C3.58651 16.4184 3.57702 16.7909 3.79579 17.0272C3.90527 17.1424 4.05732 17.2114 4.21102 17.2153L11.3057 17.3962L10.9289 32.1769C10.9206 32.5021 11.1799 32.7749 11.505 32.7832L19.7822 32.9942C20.1074 33.0025 20.3802 32.7433 20.3885 32.4181L20.7654 17.6374L27.8601 17.8183C28.1853 17.8266 28.4582 17.5673 28.4664 17.2421C28.4705 17.0825 28.4151 16.9332 28.3057 16.818Z"
                  fill="white"
                />
                <path
                  d="M21.8421 39.1648L33.2966 51.3557C33.526 51.5877 33.8925 51.5991 34.13 51.3817L46.321 39.9273C46.5587 39.704 46.5703 39.3315 46.3529 39.094C46.244 38.9782 46.0924 38.9084 45.9387 38.9036L38.8451 38.6826L39.3054 23.9043C39.3156 23.5792 39.0579 23.3049 38.7327 23.2947L30.4569 23.0369C30.1317 23.0268 29.8574 23.2845 29.8473 23.6097L29.387 38.388L22.2934 38.167C21.9682 38.1569 21.6939 38.4146 21.6838 38.7398C21.6788 38.8993 21.7334 39.049 21.8421 39.1648Z"
                  fill="white"
                />
              </svg>
            )}
          </div>
        )}

        <div className=" text-left">
          <Delta className="mb-5">{title}</Delta>
          <Body small>{props.children}</Body>
        </div>
      </div>
      {(showPrimary || buttons?.secondary) && (
        <div className="mt-20">
          {showPrimary && (
            <Button
              theme={buttons.primary.theme ?? null}
              onClick={() => router.push(buttons.primary.url ?? '#')}
            >
              {buttons.primary.text}
            </Button>
          )}
          {buttons.secondary && (
            <Link href={buttons.secondary.url ?? '#'}>
              <a className="w-full block text-center my-10">
                <Body small>End quiz</Body>
              </a>
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
